import { CalendarCheck, CheckCircle2, AlertTriangle, GitCommit, Compass } from 'lucide-react';
import { Link } from 'wouter';
import { useGetMyActivity } from '@edison/api-client-react';
import type {
  DeveloperActivityDay,
  DeveloperActivityProject,
} from '@edison/api-client-react';
import { EmptyState } from '@/components/ui/empty-state.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';

function formatDayHeading(dateStr: string) {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' });
}

function CaptureStrip({ capture }: { capture: { activeMachines?: number; hookOutdated?: boolean; lastSeenAt?: string | null } | undefined }) {
  const active = capture?.activeMachines ?? 0;
  const hookOutdated = capture?.hookOutdated ?? false;

  if (active === 0) {
    return (
      <div className="mb-6 flex items-center gap-3 rounded-[12px] border border-[oklch(0.65_0.14_65)]/40 bg-[oklch(0.65_0.14_65)]/10 px-4 py-3">
        <AlertTriangle className="h-4 w-4 text-[oklch(0.55_0.14_65)] shrink-0" aria-hidden="true" />
        <span className="flex-1 text-sm text-foreground">No machine registered — set up capture to start logging your work.</span>
        <Link href="/developer/setup">
          <span className="text-xs font-medium text-[oklch(0.44_0.09_262)] underline shrink-0">My Setup</span>
        </Link>
      </div>
    );
  }
  if (hookOutdated) {
    return (
      <div className="mb-6 flex items-center gap-3 rounded-[12px] border border-[oklch(0.65_0.14_65)]/40 bg-[oklch(0.65_0.14_65)]/10 px-4 py-3">
        <AlertTriangle className="h-4 w-4 text-[oklch(0.55_0.14_65)] shrink-0" aria-hidden="true" />
        <span className="flex-1 text-sm text-foreground">Hook outdated — update it in My Setup so your work keeps being captured.</span>
        <Link href="/developer/setup">
          <span className="text-xs font-medium text-[oklch(0.44_0.09_262)] underline shrink-0">My Setup</span>
        </Link>
      </div>
    );
  }
  return (
    <div className="mb-6 flex items-center gap-3 rounded-[12px] border border-[oklch(0.55_0.14_155)]/30 bg-[oklch(0.55_0.14_155)]/10 px-4 py-3">
      <CheckCircle2 className="h-4 w-4 text-[oklch(0.50_0.14_155)] shrink-0" aria-hidden="true" />
      <span className="flex-1 text-sm text-foreground">Capture active — your Claude Code work is being logged.</span>
    </div>
  );
}

function ProjectCard({ project }: { project: DeveloperActivityProject }) {
  const commits = project.commits ?? [];
  const explorations = project.explorations ?? [];
  const sessionCount = project.sessionCount ?? 0;
  const hours = project.hours ?? 0;

  return (
    <div className="rounded-[14px] border border-border bg-card px-4 py-3">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <span className="text-sm font-medium text-foreground">{project.project ?? '(no project)'}</span>
        <span className="text-xs text-muted-foreground font-mono shrink-0">{hours}h captured</span>
      </div>

      {commits.length > 0 && (
        <ul className="space-y-1">
          {commits.map((c, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-foreground">
              <GitCommit className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
              <span className="min-w-0 break-words">{c.message}</span>
            </li>
          ))}
        </ul>
      )}

      {explorations.length > 0 && (
        <ul className="mt-2 space-y-1">
          {explorations.map((e, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Compass className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              <span className="min-w-0 break-words italic">{e.descriptor}</span>
            </li>
          ))}
        </ul>
      )}

      {commits.length === 0 && explorations.length === 0 && sessionCount > 0 && (
        <p className="text-sm text-muted-foreground">
          {sessionCount} Claude Code {sessionCount === 1 ? 'session' : 'sessions'} — no commits landed
        </p>
      )}
      {sessionCount > 0 && (commits.length > 0 || explorations.length > 0) && (
        <p className="mt-2 text-xs text-muted-foreground">
          from {sessionCount} Claude Code {sessionCount === 1 ? 'session' : 'sessions'}
        </p>
      )}
    </div>
  );
}

function DaySection({ day }: { day: DeveloperActivityDay }) {
  const projects = day.projects ?? [];
  return (
    <section>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
        {day.date ? formatDayHeading(day.date) : ''}
      </h2>
      <div className="space-y-2">
        {projects.map((p, i) => <ProjectCard key={`${p.project}-${i}`} project={p} />)}
      </div>
    </section>
  );
}

export function DeveloperMyWorkPage() {
  const { data, isLoading, error, refetch } = useGetMyActivity({ range: 'fortnight' });
  const activity = data?.data;
  const days = activity?.days ?? [];

  return (
    <div className="max-w-[1320px] mx-auto px-8 py-6 lg:px-10 animate-page">
      <div className="mb-8">
        <h1 className="text-[22px] font-semibold tracking-tight text-foreground mb-1">My Work</h1>
        <p className="text-sm text-muted-foreground">A day-by-day log of what you actually worked on</p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
        </div>
      ) : error ? (
        <InlineError message="Failed to load your work log." onRetry={() => refetch()} />
      ) : (
        <>
          <CaptureStrip capture={activity?.capture} />
          {days.length === 0 ? (
            <EmptyState
              icon={<CalendarCheck className="h-8 w-8" />}
              title="Nothing captured yet"
              body="Your work appears here once your Claude Code hook is active and capturing sessions."
              action={
                <Link href="/developer/setup">
                  <span className="text-xs text-[oklch(0.44_0.09_262)] underline">Set up capture</span>
                </Link>
              }
            />
          ) : (
            <div className="space-y-6">
              {days.map((d, i) => <DaySection key={d.date ?? i} day={d} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
